import * as React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import * as uuid from 'uuid';
import { resolve } from 'inversify-react';
import { Row, Col, Tabs, Divider, Alert, Layout, Affix } from 'antd';

import { DataLocalService } from 'src/core/services/data-local.service';
import { FormatService } from 'src/core/services/format.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { SearchView } from 'src/app/bookings/search/search.view';
import { SearchResultView } from 'src/app/bookings/search/results/search-results.view';
import { ZuluIcon } from 'src/app/shared/icon';
import { CommandBar } from 'src/core/ui/command-bar';
import { SearchFacetView } from './search-facets.view';

const { Content, Sider } = Layout;

interface Props {
	setBusy(isBusy: boolean);
	paxs: PersonOccupancy[];
	occupancy: Occupancy[];
	searchAccommodation(item: SearchAccommodation): Promise<ApiResult>;
	searchGeneral(item: SearchGeneral): Promise<ApiResult>;
	searchTransfer(item: SearchTransfer): Promise<ApiResult>;
	searchFlight(item: SearchFlight): Promise<ApiResult>;
	searchCarRent(item: SearchCarRent): Promise<ApiResult>;
	searchTour(item: SearchTour): Promise<ApiResult>;
	ppDataService: DataSet<ProductProviderItem>;
	onProductSelected: (selection: ResultSelection[], search: Search) => void;
	onSearchSelected: () => void;
	onOpen: () => void;
	searchPreferences: SearchPreferences;
	onSearch: (search: Search) => void;
	searchProducts: boolean;
	bookingDate: Date;
	languageCode: string;
	marketId: number;
	showSearchText?: boolean;
	fieldsTaxonomy: TaxonomicField[];
	filterTaxonomies: (productType?: SearchType, productCategoryId?: number) => Promise<TaxonomicField[]>;
	taxEditor?: React.ReactNode;
	disableAddButton?: (val: boolean) => void;
}

interface State {
	collapsed: boolean;
}

@observer
export class SearchProductsView extends React.Component<Props, State> {
	public state: State = {
		collapsed: true,
	};

	@observable
	private dataSet: DataLocalService<ProductSearchResult>;

	@observable
	private count = 0;

	@observable
	private messages: DataLocalService<Message>;

	@resolve
	private formatService: FormatService;

	private yourSearch: string;

	@observable
	private currentSearch: Search;

	@observable
	public facets: Facet[];

	@observable
	private showResult: boolean;

	@resolve
	private toast: ToastNotifications;

	constructor(props: Readonly<Props>) {
		super(props);

		this.props.onOpen();
	}

	public componentWillReceiveProps(nextProps: Readonly<Props>): void {
		if (nextProps.searchProducts == true && nextProps.searchProducts != this.props.searchProducts) {
			this.props.onOpen();
			this.onSearchClick(this.currentSearch);
		}
	}

	public render(): React.ReactNode {
		const {
			children,
			fieldsTaxonomy,
			paxs,
			setBusy,
			onSearchSelected,
			searchPreferences,
		} = this.props;

		const body = this.currentSearch
			? (
				<SearchView
					currentSearch={this.currentSearch}
					fieldsTaxonomy={fieldsTaxonomy}
					onChangeCategory={this.handleChangeCategory}
					setBusy={setBusy}
					paxs={paxs}
				/>
			)
			: (
				<Alert
					type="info"
					showIcon={true}
					message="Select a product type to search for"
					style={{ position: "absolute", left: 200, top: 0 }}
				/>
			);

		return (
			<Row>
				<Col xs={24}>
					{this.renderSearch(body)}
					<Divider />
					<Affix offsetBottom={20}>
						<CommandBar
							mode="edit"
							items={children as any}
							farItems={
								[
									{
										key: 'sort',
										name: 'Sort by Price',
										icon: searchPreferences.direction === 'Asc' ? 'down' : 'up',
										onClick: () => {
											searchPreferences.direction = searchPreferences.direction === 'Asc'
												? 'Desc'
												: 'Asc';
										},
									},
									{
										key: 'pageSize',
										name: `Page size (${searchPreferences.pageSize})`,
										options: [
											{
												label: "25",
												value: 25,
												selected: searchPreferences.pageSize === 25,
											},
											{
												label: "50",
												value: 50,
												selected: searchPreferences.pageSize === 50,
											}
										],
										itemType: 3,
										onClick: () => {
											searchPreferences.pageSize = 50;
										},
									},
									{
										key: 'previousSearch',
										name: 'Previous search',
										icon: 'play-circle',
										onClick: () => {
											this.currentSearch.facets = [];
											this.facets = null;
											this.currentSearch.searchInCache = false;
											this.currentSearch.searchId = uuid.v4();
											this.showResult = false;
											searchPreferences.pageIndex = 0;
											onSearchSelected();

										},
										disabled: this.showResult == false || this.currentSearch == null
									},
								]
							}
						/>
					</Affix>
				</Col>
			</Row>
		);
	}

	private renderSearch(body: React.ReactNode): React.ReactNode {
		const {
			bookingDate,
			languageCode,
			marketId,
			onProductSelected,
			setBusy,
		} = this.props;

		if (this.showResult) {
			if (this.dataSet) {
				return (
					<Layout>
						<Layout style={{ backgroundColor: "#fff" }}>
							<Content style={{ margin: '0 16px' }}>
								<SearchResultView
									fullSreen={false}
									showAllExpanded={true}
									typeSearch={this.currentSearch.type}
									currentSearch={this.currentSearch}
									mode="display"
									setBusy={setBusy}
									yourSearch={this.yourSearch}
									model={this.dataSet}
									count={this.count}
									onProductSelected={onProductSelected}
									messages={this.messages}
									bookingDate={bookingDate}
									languageCode={languageCode}
									marketId={marketId}
									taxEditor={this.props.taxEditor}
									disableAddButton={this.props.disableAddButton}
									onPagingSearch={this.onPagingSearchClick}
								/>
							</Content>
						</Layout>
						{this.renderSider()}
					</Layout>
				);
			}
		} else {
			return (
				<React.Fragment>
					<Tabs
						onChange={this.createSearch}
						activeKey={this.currentSearch ? this.currentSearch.type : undefined}
						tabPosition="left"
					>
						<Tabs.TabPane key="Accommodation" tab={<span><ZuluIcon type="icon-hotel" />Accommodations</span>}>
							{this.currentSearch && this.currentSearch.type === "Accommodation" && body}
						</Tabs.TabPane>
						<Tabs.TabPane key="CarRent" tab={<span><ZuluIcon type="car" />Car Rents</span>}>
							{this.currentSearch && this.currentSearch.type === "CarRent" && body}
						</Tabs.TabPane>
						<Tabs.TabPane key="Transfer" tab={<span><ZuluIcon type="icon-train" />Transfers</span>}>
							{this.currentSearch && this.currentSearch.type === "Transfer" && body}
						</Tabs.TabPane>
						<Tabs.TabPane key="Flights" tab={<span><ZuluIcon type="icon-plane" />Flights</span>}>
							{this.currentSearch && this.currentSearch.type === "Flights" && body}
						</Tabs.TabPane>
						<Tabs.TabPane key="General" tab={<span><ZuluIcon type="tags" />Generals</span>}>
							{this.currentSearch && this.currentSearch.type === "General" && body}
						</Tabs.TabPane>
						<Tabs.TabPane key="Tour" tab={<span><ZuluIcon type="icon-tour" />Tours</span>}>
							{this.currentSearch && this.currentSearch.type === "Tour" && body}
						</Tabs.TabPane>
					</Tabs>
					{this.currentSearch ? null : body}
				</React.Fragment>
			);
		}
	}

	private renderSider(): React.ReactNode {
		if (this.dataSet == null || this.dataSet.count === 0 || this.facets == null || this.facets.length === 0) {
			return null;
		}

		const { collapsed } = this.state;

		return (
			<Sider
				collapsible
				collapsed={collapsed}
				onCollapse={this.toggle}
				reverseArrow={true}
				trigger={null}
				theme="light"
				collapsedWidth={65}
				width={250}
			>
				<div className="logo light" onClick={this.toggle}>
					<ZuluIcon type={collapsed ? 'menu-fold' : 'menu-unfold'} />
				</div>
				{this.renderFacets()}
			</Sider>
		);
	}

	private renderFacets = (): React.ReactNode => {
		return (
			<SearchFacetView
				model={this}
				formatService={this.formatService}
				onFacetSearchClick={this.onFacetSearchClick}
				setBusy={this.props.setBusy}
			/>
		);
	}

	private async makeProductSearch(type: SearchType, apiNames: Api[]): Promise<Search & SearchDates> {
		this.props.setBusy(true);
		const fieldsTaxonomy = await this.props.filterTaxonomies(type);
		this.props.setBusy(false);

		return {
			apiNames,
			type,
			isActive: true,
			occupancies: this.props.occupancy,
			productCategoryId: null,
			productServiceTypeId: null,
			productId: null,
			searchInCache: false,
			searchId: uuid.v4(),
			startDate: moment.utc(this.props.bookingDate).startOf('day').toDate(),
			endDate: moment.utc(this.props.bookingDate).add(1, 'day').startOf('day').toDate(),
			regionId: null,
			taxonomies: fieldsTaxonomy
				.map(m => ({
					jsonSchemaPropertyName: m.jsonSchemaPropertyName,
					tags: []
				})),
			facets: []
		};
	}

	private onFacetSearchClick = () => {
		this.currentSearch.searchInCache = true;
		this.currentSearch.facets = this.facets;
		this.props.searchPreferences.pageIndex = 0;
		this.onSearchClick(this.currentSearch);
	}

	private onPagingSearchClick = (): Promise<void> => {
		this.currentSearch.searchInCache = true;
		this.props.searchPreferences.pageIndex++;
		return this.onSearchClick(this.currentSearch, true);
	}

	private getAllHeading(headings: Heading[]): Heading[] {
		let headingscheck = [];
		for (let h of headings) {
			headingscheck.push(h);
			if (h.subHeading) {
				let arr = this.getAllHeading(h.subHeading);
				headingscheck = headingscheck.concat(arr);
			}
		}
		return headingscheck;
	}

	private updateFacets = (facets: Facet[]) => {
		//if (this.currentSearch.searchInCache == true) {
		//	for (let facet of this.currentSearch.facets) {
		//		let headingChecked = facet.headings;
		//		let facetFromServer = facets.find(f => f.name == facet.name);
		//		if (facetFromServer != null) {
		//			let headingsFromServer = this.getAllHeading(facetFromServer.headings);
		//			let headingFromServer = headingsFromServer.find(h => headingChecked.some(hc => hc.value == h.value))
		//			if (headingFromServer)
		//				headingFromServer.isChecked = true;
		//		}
		//	}

		//}
		this.facets = facets;
	}

	private onSearchClick = (item: Search, paging?: boolean) => {
		this.yourSearch = '';
		let paxs = 0;
		item.occupancies.forEach((occ) => {
			paxs = paxs + occ.paxs.length;
		})
		switch (item.type) {
			case 'Accommodation':
				{
					let accommodation = item as SearchAccommodation;
					this.props.setBusy(true);
					return this.props.searchAccommodation(accommodation)
						.then(res => {
							this.yourSearch = 'Where product Name is '.concat(accommodation.productName ? accommodation.productName.concat(',  Region Name is ') : ' Any, Region Name is ').concat(accommodation.regionName ? accommodation.regionName.concat(', Category is ') : ' Any,  Category is  ').concat(accommodation.productCategoryDisplayName ? accommodation.productCategoryDisplayName.concat(' From ') : ' Any From ').concat(this.formatService.toString('small-date', accommodation.startDate, "utc")).concat(' To ').concat(this.formatService.toString('small-date', accommodation.endDate, "utc")).concat(' Who ').concat(accommodation.occupancies.length.toString()).concat(' room(s) for ').concat(paxs.toString()).concat(' passenger(s)');
							this.processResult(res, paging);
						})
						.catch(this.processError);
					break;
				}
			case 'CarRent':
				{
					let model = item as SearchCarRent;
					this.props.setBusy(true);
					return this.props.searchCarRent(model)
						.then(res => {
							this.yourSearch = 'Where Region Name is '.concat(model.regionName ? model.regionName.concat(', ') : ' Any, ').concat(' Pickup from ').concat(model.fromPlace ? model.fromPlace : ' Any ').concat(' on ').concat(this.formatService.toString('small-date-time', model.startDate, "utc")).concat(' Drop off at ').concat(model.toPlace ? model.toPlace : ' Any ').concat(' on ').concat(this.formatService.toString('small-date-time', model.endDate, "utc"));
							this.processResult(res, paging);
						})
						.catch(this.processError);
					break;
				}
			case 'Transfer':
				{
					let model = item as SearchTransfer;
					this.props.setBusy(true);
					return this.props.searchTransfer(model)
						.then(res => {
							this.yourSearch = 'From '.concat(model.fromPlace ? model.fromPlace : ' Any ').concat(' on ').concat(this.formatService.toString('small-date-time', model.startDate, "utc")).concat(' to ').concat(model.toPlace ? model.toPlace : ' Any ').concat(' on ').concat(this.formatService.toString('small-date-time', model.endDate, "utc"));
							this.processResult(res, paging);
						})
						.catch(this.processError);
					break;
				}
			case 'General':
				{
					let model = item as SearchGeneral;
					this.props.setBusy(true);
					return this.props.searchGeneral(model)
						.then(res => {
							this.yourSearch = 'From '.concat(this.formatService.toString('small-date', model.startDate, "utc")).concat(' to ').concat(this.formatService.toString('small-date', model.endDate, "utc"));
							this.processResult(res, paging);
						})
						.catch(this.processError);
					break;
				}
			case 'Flights':
				{
					let model = item as SearchFlight;
					this.props.setBusy(true);
					return this.props.searchFlight(model)
						.then(res => {
							this.yourSearch = '';
							this.processResult(res, paging);
						})
						.catch(this.processError);
					break;
				}
			case 'Tour':
				{
					let model = item as SearchTour;
					this.props.setBusy(true);
					return this.props.searchTour(model)
						.then(res => {
							this.yourSearch = 'From '.concat(this.formatService.toString('small-date', model.startDate, "utc")).concat(' to ').concat(this.formatService.toString('small-date', model.endDate, "utc"));
							this.processResult(res, paging);
						})
						.catch(this.processError);
					break;
				}
		}
	};

	private processResult = (res: ApiResult, paging?: boolean): void => {
		if (paging) {
			this.dataSet.items.push(...res.items);
			this.messages.items.push(...res.messages);
		} else {
			this.dataSet = new DataLocalService(res.items);
			this.messages = new DataLocalService(res.messages);
		}
		//this.dataSet = new DataLocalService(paging ? this.dataSet.items.concat(res.items) : res.items);
		//this.messages = new DataLocalService(paging ? this.messages.items.concat(res.messages) : res.messages);
		this.count = res.count;
		if (res.facets && res.facets.length > 0) {
			this.updateFacets(res.facets);
		}
		this.currentSearch.searchInCache = false;
		this.props.onSearch(this.currentSearch);
		this.showResult = true;
		this.props.setBusy(false);
	}

	private processError = (error) => {
		this.currentSearch.searchInCache = false;
		this.dataSet = null;
		this.toast.showToast('Error in search request!', ObjectUtils.parseError(error), 'error');
		this.props.onSearchSelected();
		this.props.setBusy(false);
	}

	private createSearch = (activeKey: SearchType): void => {
		switch (activeKey) {
			case "Accommodation":
				this.createAccommodationSearch();
				break;
			case "CarRent":
				this.createRentCarSearch();
				break;
			case "Transfer":
				this.createTranferSearch();
				break;
			case "Flights":
				this.createFlightSearch();
				break;
			case "General":
				this.createGeneralSearch();
				break;
			case "Tour":
				this.createTourSearch();
				break;
		}
	}

	private createAccommodationSearch = async () => {
		const apiNames = this.props.ppDataService.items
			.filter(x => x.isActiveForApiAccommodation)
			.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForAccommodation, isActiveForApi: o.isActiveForApiAccommodation }));
		this.props.onSearchSelected();

		let search: SearchAccommodation = Object.assign({}, await this.makeProductSearch('Accommodation', apiNames), {
			bypassOcuppancy: false,
			regionId: null,
			nights: 1,
			placeId: null,
		});

		this.currentSearch = observable(search);
		this.showResult = false;
	};

	private createGeneralSearch = async () => {
		const apiNames = this.props.ppDataService.items
			.filter(x => x.isActiveForApiGeneral)
			.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForGeneral, isActiveForApi: o.isActiveForApiGeneral }));
		this.props.onSearchSelected();

		const search: SearchGeneral = Object.assign({}, await this.makeProductSearch('General', apiNames), {
			regionId: null,
		});

		this.currentSearch = observable(search);
		this.showResult = false;
	};

	private createTourSearch = async () => {
		const apiNames = this.props.ppDataService.items
			.filter(x => x.isActiveForApiTour)
			.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForTour, isActiveForApi: o.isActiveForApiTour }));
		this.props.onSearchSelected();

		const search: SearchTour = Object.assign({}, await this.makeProductSearch('Tour', apiNames), {
			regionId: null,
		});

		this.currentSearch = observable(search);
		this.showResult = false;
	}

	private createRentCarSearch = async () => {
		const apiNames = this.props.ppDataService.items
			.filter(x => x.isActiveForApiCarRent)
			.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForCarRent, isActiveForApi: o.isActiveForApiCarRent }));
		this.props.onSearchSelected();

		const search: SearchCarRent = Object.assign({}, await this.makeProductSearch('CarRent', apiNames), {
			regionId: null,
			days: 1,
			supplierId: null,
			fromPlaceId: null,
			toPlaceId: null
		});

		this.currentSearch = observable(search);
		this.showResult = false;
	};

	private createTranferSearch = async () => {
		const apiNames = this.props.ppDataService.items
			.filter(x => x.isActiveForApiTransfer)
			.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForTransfer, isActiveForApi: o.isActiveForApiTransfer }));
		this.props.onSearchSelected();

		const search: SearchTransfer = Object.assign({}, await this.makeProductSearch('Transfer', apiNames), {
			fromPlaceId: null,
			toPlaceId: null,
			returnJourney: false,
			returnDate: null,
		});

		this.currentSearch = observable(search);
		this.showResult = false;
	};

	private createFlightSearch = async () => {
		let flights: SearchFlightLeg[] = [];
		let endDate = moment.utc(this.props.bookingDate).startOf('day');
		endDate.add(23, 'hours');
		endDate.add(59, 'minutes');
		let flightLeg: SearchFlightLeg = {
			id: uuid.v4(),
			startDate: moment.utc(this.props.bookingDate).startOf('day').toDate(),
			endDate: endDate.toDate(),
			fromPlaceId: null,
			toPlaceId: null,
			nights: 0,
			airlineId: null,
		};
		flights.push(flightLeg);
		this.props.onSearchSelected();

		this.props.setBusy(true);
		const fieldsTaxonomy = await this.props.filterTaxonomies('Flights');

		let search: SearchFlight = {
			type: 'Flights',
			isActive: true,
			flights: flights,
			occupancies: this.props.occupancy,
			lastTicketingDate: new Date(),
			nonStopOverCount: 0,
			publicFares: false,
			searchInCache: false,
			searchId: uuid.v4(),
			directOnly: false,
			negociatedFares: false,
			maxStopOverDurationInMinutes: 0,
			flightDurationInMinutes: 0,
			facets: [],
			taxonomies: fieldsTaxonomy
				.map(m => ({
					jsonSchemaPropertyName: m.jsonSchemaPropertyName,
					tags: []
				})),
			apiNames: this.props.ppDataService.items
				.filter(x => x.isActiveForApiFlight)
				.map(o => ({ apiName: o.apiName, name: o.name, isActive: o.isActiveForFlight, isActiveForApi: o.isActiveForApiFlight }))
		};

		this.props.setBusy(false);
		this.currentSearch = observable(search);
		this.showResult = false;
	};

	private handleChangeCategory = async (productCategoryId?: number): Promise<void> => {
		if (this.currentSearch == null) {
			return;
		}

		this.props.setBusy(true);

		const taxonomies = await this.props.filterTaxonomies(this.currentSearch.type, productCategoryId);
		this.currentSearch.taxonomies = taxonomies
			.map(m => {
				const taxonomy = this.currentSearch.taxonomies.find(p => p.jsonSchemaPropertyName === m.jsonSchemaPropertyName);
				const tags = taxonomy == null ? [] : taxonomy.tags;

				return {
					jsonSchemaPropertyName: m.jsonSchemaPropertyName,
					tags: tags
				};
			});

		this.props.setBusy(false);
	};

	private toggle = () => {
		this.setState({
			collapsed: !this.state.collapsed,
		});
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/search-products.view.tsx